import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ExternalLink, Loader2, X } from 'lucide-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { isScrapableWebsite } from '@/lib/scrapableHosts'
import { Candidate, REJECT_REASONS, apiError, lisbonDay } from './types'

function ModalShell({
  title,
  onClose,
  children,
  footer,
  busy,
}: {
  title: string
  onClose: () => void
  children: React.ReactNode
  footer: React.ReactNode
  busy?: boolean
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={busy ? undefined : onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 pb-0">
          <h3 className="text-lg font-semibold text-secondary-900">{title}</h3>
          <button onClick={onClose} disabled={busy} aria-label="Close" className="p-1 rounded-lg hover:bg-secondary-100 text-secondary-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        <div className="flex items-center justify-end gap-3 p-6 pt-0">{footer}</div>
      </div>
    </div>
  )
}

const Warning = ({ children, tone = 'amber' }: { children: React.ReactNode; tone?: 'amber' | 'red' | 'blue' }) => {
  const colors = {
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
  }[tone]
  return (
    <div className={`flex items-start gap-2 text-sm border rounded-lg p-3 ${colors}`}>
      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <div>{children}</div>
    </div>
  )
}

// ----------------------------------------------------------------------------
// Approve
// ----------------------------------------------------------------------------

export function ApproveModal({
  candidate,
  onClose,
  onDone,
}: {
  candidate: Candidate
  onClose: () => void
  onDone: () => void
}) {
  // Developments the broker site could only describe by slug are completed from the
  // broker page by GET /discovery/candidates/:id before the form is pre-filled.
  const { data, isLoading } = useQuery<Candidate>({
    queryKey: ['discovery-candidate', candidate.candidate_id],
    queryFn: async () => (await api.get(`/discovery/candidates/${candidate.candidate_id}`)).data,
    enabled: candidate.needs_enrichment,
    staleTime: Infinity,
    retry: false,
  })

  if (candidate.needs_enrichment && isLoading) {
    return (
      <ModalShell
        title="Approve development"
        onClose={onClose}
        footer={
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        }
      >
        <div className="flex items-center gap-2 text-sm text-secondary-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Reading the details from the broker website…
        </div>
      </ModalShell>
    )
  }

  return <ApproveForm candidate={{ ...candidate, ...(data ?? {}) }} onClose={onClose} onDone={onDone} />
}

function ApproveForm({
  candidate,
  onClose,
  onDone,
}: {
  candidate: Candidate
  onClose: () => void
  onDone: () => void
}) {
  const hasUnits = candidate.is_live !== false && (candidate.available_fractions === null || candidate.available_fractions > 0)
  const suggested =
    candidate.match_reason === 'name_exact' && candidate.name
      ? `${candidate.name} (${candidate.broker_name})`
      : candidate.name ?? ''
  const [name, setName] = useState(suggested)
  const [city, setCity] = useState(candidate.city ?? '')
  const [district, setDistrict] = useState(candidate.district ?? '')
  const [neighbourhood, setNeighbourhood] = useState(candidate.neighbourhood ?? '')
  const [listingDate, setListingDate] = useState(
    candidate.published_at ? lisbonDay(new Date(candidate.published_at)) : lisbonDay(new Date()),
  )
  const [scrapeNow, setScrapeNow] = useState(hasUnits && candidate.scrapable)
  const [problem, setProblem] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/discovery/candidates/${candidate.candidate_id}/approve`, {
        name: name.trim(),
        city: city.trim() || undefined,
        district: district.trim() || undefined,
        neighbourhood: neighbourhood.trim() || undefined,
        listing_date: listingDate || undefined,
        scrape_now: scrapeNow,
      })
      return res.data
    },
    onSuccess: (data) => {
      const warning = data?.scrape?.warning
      toast.success(`Project "${data.project?.name}" created${data?.scrape?.triggered ? ' and scraping started' : ''}`)
      if (warning) toast(warning, { icon: '⚠️' })
      onDone()
    },
    onError: (error) => {
      const e = apiError(error)
      setProblem(e.message)
      if (e.code === 'NAME_COLLISION' && e.suggested_name) setName(e.suggested_name)
    },
  })

  return (
    <ModalShell
      title="Approve development"
      onClose={onClose}
      busy={mutation.isPending}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={mutation.isPending || !name.trim() || !candidate.scrapable}
            onClick={() => {
              setProblem(null)
              mutation.mutate()
            }}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Create project
          </button>
        </>
      }
    >
      <div className="text-sm text-secondary-600">
        Creates a project for <span className="font-medium text-secondary-900">{candidate.broker_name}</span> pointing at{' '}
        <a href={candidate.canonical_url} target="_blank" rel="noreferrer" className="text-primary-700 underline break-all">
          {candidate.canonical_url}
          <ExternalLink className="inline h-3 w-3 ml-1" />
        </a>
      </div>

      {!candidate.scrapable && (
        <Warning tone="red">
          The existing scrapers cannot read this development (no anchor listing on the broker site). It cannot be approved yet.
        </Warning>
      )}
      {!isScrapableWebsite(candidate.canonical_url) && candidate.scrapable && (
        <Warning>This host is not in the scraper allow-list; “Scrap now” will be refused.</Warning>
      )}
      {!hasUnits && (
        <Warning>
          No units are listed for sale yet. The scraper will report an error in Monitoring until units are published; snoozing is usually
          better.
        </Warning>
      )}
      {candidate.flags.some((f) => f.startsWith('doubt:')) && (
        <Warning>This may not be residential ({candidate.flags.filter((f) => f.startsWith('doubt:')).map((f) => f.slice(6)).join(', ')}). Check the broker page first.</Warning>
      )}
      {candidate.match_project_id && (
        <Warning tone="blue">
          Similar existing project:{' '}
          <Link to={`/projects/${candidate.match_project_id}`} className="underline font-medium" target="_blank">
            {candidate.match_name}
          </Link>{' '}
          ({candidate.match_broker_name ?? 'no broker'}
          {candidate.match_sold_out ? ', sold out' : ''}). If it is the same development under the same broker, use “Link to project”
          instead.
        </Warning>
      )}
      {problem && <Warning tone="red">{problem}</Warning>}

      <div className="space-y-3">
        <div>
          <label className="form-label">Project name</label>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={255} />
          <p className="text-xs text-secondary-500 mt-1">Names are unique. Co-marketed developments carry the broker in brackets.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">City (concelho)</label>
            <input className="form-input" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div>
            <label className="form-label">District (distrito)</label>
            <input className="form-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Neighbourhood (freguesia)</label>
            <input className="form-input" value={neighbourhood} onChange={(e) => setNeighbourhood(e.target.value)} />
          </div>
          <div>
            <label className="form-label">Listing date</label>
            <input type="date" className="form-input" value={listingDate} onChange={(e) => setListingDate(e.target.value)} />
            <p className="text-xs text-secondary-500 mt-1">
              {candidate.published_at ? "Broker's publication date" : 'Not published by the broker: today'}
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-secondary-700">
          <input type="checkbox" checked={scrapeNow} disabled={!candidate.scrapable} onChange={(e) => setScrapeNow(e.target.checked)} />
          Start scraping right after creating the project
        </label>
      </div>
    </ModalShell>
  )
}

// ----------------------------------------------------------------------------
// Link to an existing project of the same broker
// ----------------------------------------------------------------------------

export function LinkModal({
  candidate,
  onClose,
  onDone,
}: {
  candidate: Candidate
  onClose: () => void
  onDone: () => void
}) {
  const [term, setTerm] = useState(candidate.match_name ?? candidate.name ?? '')
  const [debounced, setDebounced] = useState(term)
  const sameBrokerMatch = candidate.match_broker_name !== null && candidate.match_broker_name === candidate.broker_name
  const [selected, setSelected] = useState<number | null>(sameBrokerMatch ? candidate.match_project_id : null)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 300)
    return () => clearTimeout(t)
  }, [term])

  const { data, isFetching } = useQuery({
    queryKey: ['discovery-link-search', candidate.broker_id, debounced],
    queryFn: async () => {
      const res = await api.get('/projects', { params: { search: debounced, broker_id: candidate.broker_id, limit: 10, page: 1 } })
      return (res.data?.data ?? []) as Array<{ project_id: number; name: string; website: string; sold_out: boolean }>
    },
    enabled: debounced.trim().length >= 2,
  })

  const options = useMemo(() => data ?? [], [data])

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/discovery/candidates/${candidate.candidate_id}/link`, { project_id: selected })
      return res.data
    },
    onSuccess: (data) => {
      toast.success(
        data.url_changed
          ? `Project URL updated${data?.scrape?.triggered ? ' and re-scrape requested' : ''}`
          : 'Linked to existing project',
      )
      if (data?.scrape?.warning) toast(data.scrape.warning, { icon: '⚠️' })
      onDone()
    },
    onError: (error) => setProblem(apiError(error).message),
  })

  return (
    <ModalShell
      title="Link to an existing project"
      onClose={onClose}
      busy={mutation.isPending}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button className="btn-primary" disabled={!selected || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Link and update URL
          </button>
        </>
      }
    >
      <p className="text-sm text-secondary-600">
        Use this when the development already exists under <span className="font-medium">{candidate.broker_name}</span> but with an old
        or different URL. The project keeps its units and history and is re-pointed to:
      </p>
      <p className="text-xs text-secondary-700 break-all bg-secondary-50 border border-secondary-200 rounded p-2">{candidate.canonical_url}</p>
      {problem && <Warning tone="red">{problem}</Warning>}
      <div>
        <label className="form-label">Search {candidate.broker_name} projects</label>
        <input className="form-input" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Project name" />
      </div>
      <div className="border border-secondary-200 rounded-lg divide-y divide-secondary-100 max-h-60 overflow-y-auto">
        {isFetching && <div className="p-3 text-sm text-secondary-500">Searching…</div>}
        {!isFetching && options.length === 0 && <div className="p-3 text-sm text-secondary-500">No projects found.</div>}
        {options.map((p) => (
          <label key={p.project_id} className="flex items-start gap-2 p-3 cursor-pointer hover:bg-secondary-50">
            <input
              type="radio"
              className="mt-1"
              checked={selected === Number(p.project_id)}
              onChange={() => setSelected(Number(p.project_id))}
            />
            <span className="text-sm">
              <span className="font-medium text-secondary-900">{p.name}</span>
              {p.sold_out && <span className="ml-2 text-xs text-red-600">sold out</span>}
              <span className="block text-xs text-secondary-500 break-all">{p.website}</span>
            </span>
          </label>
        ))}
      </div>
    </ModalShell>
  )
}

// ----------------------------------------------------------------------------
// Reject (one or many)
// ----------------------------------------------------------------------------

export function RejectModal({
  candidates,
  onClose,
  onDone,
}: {
  candidates: Candidate[]
  onClose: () => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('not_residential')
  const [note, setNote] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async (): Promise<{ requested: number; changed: number }> => {
      if (candidates.length === 1) {
        await api.post(`/discovery/candidates/${candidates[0].candidate_id}/reject`, { reason, note: note || undefined })
        return { requested: 1, changed: 1 }
      }
      const res = await api.post('/discovery/candidates/bulk', {
        ids: candidates.map((c) => c.candidate_id),
        action: 'reject',
        reason,
        note: note || undefined,
      })
      return { requested: res.data.requested, changed: res.data.changed }
    },
    onSuccess: ({ requested, changed }) => {
      if (changed < requested) toast(`${changed} of ${requested} rejected; the rest had already been handled`, { icon: '⚠️' })
      else toast.success(requested === 1 ? 'Rejected' : `${changed} rejected`)
      onDone()
    },
    onError: (error) => setProblem(apiError(error).message),
  })

  return (
    <ModalShell
      title={candidates.length === 1 ? `Reject “${candidates[0].name ?? 'development'}”` : `Reject ${candidates.length} developments`}
      onClose={onClose}
      busy={mutation.isPending}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button className="btn-danger" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Reject
          </button>
        </>
      }
    >
      <p className="text-sm text-secondary-600">
        Rejected developments are not proposed again. You can reopen them from the Rejected tab.
      </p>
      {problem && <Warning tone="red">{problem}</Warning>}
      <div>
        <label className="form-label">Reason</label>
        <select className="form-input" value={reason} onChange={(e) => setReason(e.target.value)}>
          {REJECT_REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        {reason === 'sold_out' && (
          <p className="text-xs text-secondary-500 mt-1">If the broker lists units for sale again, it comes back to New automatically.</p>
        )}
      </div>
      <div>
        <label className="form-label">Note (optional)</label>
        <input className="form-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </div>
    </ModalShell>
  )
}
